import * as React from "react"

import { cn } from "@/lib/utils"
import { VoiceInputButton, type VoiceInputTone } from "@/components/ui/voice-input-button"
import { appendVoiceChunk } from "@/lib/voice"

export interface VoiceInputConfig {
  tone?: VoiceInputTone
  label?: string
  onChange?: (value: string) => void
}

export interface InputProps extends React.ComponentProps<"input"> {
  voiceInput?: VoiceInputConfig | boolean
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, voiceInput, value, onChange, ...props }, ref) => {
    const baseClass = cn(
      "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-sm transition-colors file:border-0 file:bg-transparent file:text-sm file:font-medium file:text-foreground placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
      className
    )

    if (!voiceInput || type === "password" || type === "file" || type === "checkbox" || type === "radio" || type === "color" || type === "number" || type === "date" || type === "time" || type === "datetime-local") {
      return (
        <input
          type={type}
          className={baseClass}
          ref={ref}
          value={value}
          onChange={onChange}
          {...props}
        />
      )
    }

    const config: VoiceInputConfig = typeof voiceInput === "object" ? voiceInput : {}
    const tone: VoiceInputTone = config.tone ?? "neutral"
    const label = config.label ?? "Dictate input"

    const handleTranscript = (chunk: string) => {
      const current = typeof value === "string" ? value : ""
      const next = appendVoiceChunk(current, chunk)
      if (config.onChange) {
        config.onChange(next)
        return
      }
      if (onChange) {
        const synthetic = {
          target: { value: next },
          currentTarget: { value: next },
        } as unknown as React.ChangeEvent<HTMLInputElement>
        onChange(synthetic)
      }
    }

    return (
      <div className="relative w-full">
        <input
          type={type}
          className={cn(baseClass, "pr-9")}
          ref={ref}
          value={value}
          onChange={onChange}
          {...props}
        />
        <VoiceInputButton
          tone={tone}
          size="xs"
          onTranscript={handleTranscript}
          disabled={props.disabled}
          label={label}
          className="absolute right-1.5 top-1/2 -translate-y-1/2"
        />
      </div>
    )
  }
)
Input.displayName = "Input"

export { Input }
